// Imports for external libraries go here.
import React, { FC, useState } from 'react';

// Imports for internal (to the monorepo) libraries go here,
// separated by a blank line from external imports.
// The closer the import is to the file the lower it should be in this list.
import { ConsentsListProps } from './ConsentsList.types';
import { StyledConsentsList } from './ConsentsList.styles';
import { ConsentsCheckListModalInfo } from './ConsentsList.types';
import { toggleSelectedConsentCheckbox } from '../../../utils/consents';
import { ConsentsListItem } from './ConsentsListItem';
import { ConsentSectionType } from '../LegalConsents.types';
import { Modal } from '@marriott/mi-ui-library';
import DOMPurify from 'dompurify';

// Use named rather than default exports.
export const ConsentsList: FC<ConsentsListProps> = ({ consentState, onConsentChange, validateErrors }) => {
  const [consentModalInfo, setConsentModalInfo] = useState<ConsentsCheckListModalInfo>({
    showModal: false,
    modalHeader: '',
    modalBody: '',
  });
  const { showModal, modalHeader, modalBody } = consentModalInfo;

  //Handling popup modal
  const onModalOpen = async (_hrefLink: string, e: React.MouseEvent) => {
    e.preventDefault();
    try {
      //const authHeader = `Basic YWRtaW46YWRtaW4=`;
      const response = await fetch('https://www.marriott.com/loyalty/terms.mi');
      const responseText = await response.text();
      const parser = new DOMParser();
      const virtualDoc = parser.parseFromString(responseText, 'text/html');
      const headingText = (virtualDoc.getElementsByClassName('m-modal-header')[0] as HTMLHeadingElement)?.innerText;
      const modalBody = virtualDoc.getElementsByClassName('m-modal-body')[0];

      setConsentModalInfo({
        showModal: true,
        modalHeader: headingText,
        modalBody: modalBody,
      });
    } catch (error) {
      // Handle errors here
      console.error('Error fetching terms:', error);
    }
  };

  const sanitizedNode = DOMPurify.sanitize(modalBody, { RETURN_DOM: true }); // RETURN_DOM to resolve security issue
  const fragmentString = sanitizedNode.innerHTML; // Get the HTML content of the container

  const onModalClose = () => {
    setConsentModalInfo(prevState => ({
      ...prevState,
      showModal: false,
    }));
  };

  const onCheckboxToggle = (_selectedConsentId: string, checkboxId: string) => {
    const isAgreeAllConsentsInTheList = consentState.sectionTypes[ConsentSectionType.AgreeToAll] ? true : false;
    const updatedConsents = toggleSelectedConsentCheckbox({
      consents: consentState.list,
      selectedConsentId: checkboxId,
      isAgreeAllConsentsInTheList,
    });

    onConsentChange({ updatedConsentsList: updatedConsents });
  };

  const onLinkClickInHeaderOrCheckbox = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    // Check if the clicked target is an anchor tag within the allowed containers
    if ((target.closest('.m-header-list') || target.closest('.m-checkbox-label')) && target.tagName === 'A') {
      e.preventDefault(); // Prevent default anchor behavior
      onModalOpen(target.getAttribute('href') as string, e); // Open modal
    }
  };

  return (
    <StyledConsentsList data-testid="legal-ConsentList" data-component-name="o-legal-ConsentList">
      {consentState.list.map(consentData => (
        <ConsentsListItem
          key={`consentListItem-${consentData.id}`}
          consentData={consentData}
          showTopDivider={consentData.showTopDivider}
          showBottomDivider={consentData.showBottomDivider}
          consentSectionType={consentData.sectionType}
          validateErrors={validateErrors}
          onModalOpen={onModalOpen}
          onCheckboxToggle={onCheckboxToggle}
          onLinkClickInHeaderOrCheckbox={onLinkClickInHeaderOrCheckbox}
        />
      ))}
      {showModal && (
        <Modal
          role="dialog"
          labelledBy="termsModalLabel"
          show={showModal}
          popupOpenState={showModal}
          handleBlur={true}
          disableScrollOnBody={true}
          setPopupOpenState={onModalClose}
          onClose={onModalClose}
        >
          <Modal.Header
            labelText={modalHeader}
            customHeadingClass="t-subtitle-xl"
            closeBtnAriaLabel="close-button"
            popupHeaderOnCLoseFunc={onModalClose}
          />
          <Modal.Body className="p-5">
            <div
              dangerouslySetInnerHTML={{
                __html: fragmentString,
              }}
            ></div>
          </Modal.Body>
        </Modal>
      )}
    </StyledConsentsList>
  );
};
